import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { initials, sendCursor, onLiveCursor, type LivePerson, type LiveCursor } from './live';

// Each person gets the same colour everywhere (hue from their user id).
const hueStyle = (userId: number) => ({ ['--live-hue' as string]: String((userId * 67) % 360) });

/** Initials of the other people on this screen, in the page header. */
export function PresenceAvatars({ people }: { people: LivePerson[] }) {
  const { t } = useT();
  if (people.length === 0) return null;
  return (
    <div className="live-avatars" title={t('live.here', { names: people.map(p => p.name).join(', ') })}>
      {people.map(p => (
        <span key={p.userId} className="live-avatar" style={hueStyle(p.userId)} aria-label={p.name}>{initials(p.name)}</span>
      ))}
    </div>
  );
}

/** On a card: who is moving it or has it open right now. */
export function FocusBadge({ people }: { people: LivePerson[] | undefined }) {
  const { t } = useT();
  if (!people?.length) return null;
  return (
    <span className="live-focus">
      {people.map(p => (
        <span key={p.userId} className="live-avatar live-avatar--sm" style={hueStyle(p.userId)}
          title={t(p.focus?.action === 'drag' ? 'live.moving' : p.focus?.action === 'moved' ? 'live.moved' : 'live.viewing', { name: p.name })}>
          {initials(p.name)}{p.focus?.action === 'drag' ? ' ✋' : p.focus?.action === 'moved' ? ' ✓' : ''}
        </span>
      ))}
    </span>
  );
}

/** Where someone else is dragging a card right now: a faded copy with their initials. */
export function LiveGhost({ person, children }: { person: LivePerson; children: React.ReactNode }) {
  const { t } = useT();
  return (
    <div className="live-ghost" style={hueStyle(person.userId)} title={t('live.moving', { name: person.name })}>
      {children}
      <span className="live-focus">
        <span className="live-avatar live-avatar--sm" style={hueStyle(person.userId)}>{initials(person.name)} ✋</span>
      </span>
    </div>
  );
}

/** The element marked data-live-anchor under a point — skipping the card being dragged, which floats above. */
function anchorAt(x: number, y: number): HTMLElement | null {
  for (const el of document.elementsFromPoint(x, y)) {
    if (el.closest('[data-dnd-overlay]')) continue;
    return el.closest<HTMLElement>('[data-live-anchor]');
  }
  return null;
}

/**
 * Live pointers on a shared screen (task board, Kanban Fonction board): sends
 * this user's pointer, and draws the others' with their initials. Positions
 * are relative to the element under the pointer (data-live-anchor: a cell, a
 * column…), so they land in the same place whatever each one's screen size,
 * zoom or scroll. `people`: who is on the screen (from usePresence).
 */
export function LiveCursors({ people }: { people: LivePerson[] }) {
  const [cursors, setCursors] = useState<Record<number, LiveCursor & { at: number }>>({});
  const [, setTick] = useState(0);

  // Mine → the others.
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const el = anchorAt(e.clientX, e.clientY);
      if (!el) { sendCursor(null); return; }
      const r = el.getBoundingClientRect();
      sendCursor(el.dataset.liveAnchor!, (e.clientX - r.left) / (r.width || 1), (e.clientY - r.top) / (r.height || 1));
    };
    const leave = () => sendCursor(null);
    window.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', leave);
    window.addEventListener('blur', leave);
    return () => {
      window.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', leave);
      window.removeEventListener('blur', leave);
      sendCursor(null);
    };
  }, []);

  // The others' → drawn here; redrawn on scroll/resize, dropped when idle.
  useEffect(() => onLiveCursor(c => setCursors(prev => ({ ...prev, [c.userId]: { ...c, at: Date.now() } }))), []);
  useEffect(() => {
    let frame = 0;
    const redraw = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => setTick(n => n + 1)); };
    window.addEventListener('scroll', redraw, true);
    window.addEventListener('resize', redraw);
    const idle = setInterval(redraw, 2000);
    return () => { window.removeEventListener('scroll', redraw, true); window.removeEventListener('resize', redraw); clearInterval(idle); cancelAnimationFrame(frame); };
  }, []);

  const here = new Map(people.map(p => [p.userId, p]));
  const shown = Object.values(cursors).flatMap(c => {
    const person = here.get(c.userId);
    if (!person || !c.anchor || Date.now() - c.at > 15_000) return [];
    const el = document.querySelector<HTMLElement>(`[data-live-anchor="${CSS.escape(c.anchor)}"]`);
    if (!el) return [];
    const r = el.getBoundingClientRect();
    return [{ person, x: r.left + c.fx * r.width, y: r.top + c.fy * r.height }];
  });
  if (shown.length === 0) return null;
  return createPortal(
    <>
      {shown.map(({ person, x, y }) => (
        <div key={person.userId} className="live-cursor" style={{ ...hueStyle(person.userId), transform: `translate(${x}px, ${y}px)` }}>
          <svg width="16" height="20" viewBox="0 0 16 20" aria-hidden="true">
            <path d="M1 1 L1 16 L5 12 L8 19 L11 18 L8 11 L14 11 Z" />
          </svg>
          <span className="live-avatar live-avatar--sm">{initials(person.name)}{person.focus?.action === 'drag' ? ' ✋' : ''}</span>
        </div>
      ))}
    </>,
    document.body,
  );
}
